/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {ClickAwayListener, makeStyles} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import isNil from 'lodash.isnil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {DropdownArrowIcon} from '@ion/dls/src/icons/DropdownArrowIcon';
import {dlsFloatingCardStyle, toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  isEqualArrays,
  removeTempQueries
} from './ModularSearchWidget';
import {Label, QueryOperations} from './QueryOperations';
import SaveQueriesList from './SaveQueriesList';

const QUERY_OPERATIONS_DROPDOWN      = 'query-operations-dropdown';
const SELECT_FROM_SAVED_QUERY_BUTTON = 'select-from-saved-query';

const useStyles = makeStyles(() => ({
  selectContainer : {
    height     : 'inherit',
    display    : 'flex',
    alignItems : 'center'
  },
  popoverRoot : toggleThemeStyle({
    marginLeft : '15px'
  }, {
    width           : '286px',
    height          : 'auto',
    backgroundColor : `${dlsColor('white')} !important`,
    ...dlsFloatingCardStyle()
  }),
  selectContainerItems : {
    display    : 'flex',
    alignItems : 'center',
    height     : 40,
    position   : 'relative'
  },
  // per Figma "Button/Tertiary/IconRight" spec, current theme only - legacy
  // keeps its own existing 30px/12px sizing untouched ...
  saveBtn : {
    height       : toggleThemeStyle('30px', '32px'),
    padding      : toggleThemeStyle('0px 12px', '0px 8px'),
    width        : toggleThemeStyle(undefined, '140px'),
    borderRadius : toggleThemeStyle(undefined, '8px')
  },
  dropDownBtnText : {
    fontSize : 12
  },
  verticalDivider : {
    position   : 'absolute',
    left       : '82%',
    top        : '30%',
    bottom     : '25%',
    borderLeft : `1px solid ${dlsColor('grey3')}` as string
  },
  list : {
    display       : 'flex',
    flexDirection : 'column',
    paddingBottom : '0'
  },
  menuItem : {
    height    : toggleThemeStyle('40px', '50px'),
    padding   : toggleThemeStyle(undefined, '0 8px'),
    '&:hover' : {
      backgroundColor : toggleThemeStyle(undefined, 'transparent')
    }
  },
  saveText : {
    '&.Mui-disabled' : {
      color       : dlsColor('aaBlue'),
      fontSize    : 12,
      height      : '18px',
      lineHeight  : '20px',
      paddingLeft : '4px'
    }
  },
  undoChanges           : {},
  deleteSavedSearch     : {},
  savedQueryNameWrapper : {
    display     : 'flex',
    height      : 31,
    alignItems  : 'center',
    marginRight : 10,
    padding     : 10,
    gap         : 10
  },
  favoriteIconWrapper : {
    paddingTop : 5
  },
  savedQueryName : {
    color         : dlsColor('grey3'),
    fontSize      : 14,
    lineHeight    : 2,
    height        : 30,
    alignSelf     : 'center',
    textTransform : 'uppercase'
  },
  disabled : {
    height    : 40,
    cursor    : 'auto',
    marginTop : 5,
    '&:hover' : {
      backgroundColor : 'transparent'
    }
  },
  openText : toggleThemeStyle({
    fontSize       : '12px !important',
    color          : dlsColor('aaBlue'),
    justifyContent : 'flex-start',
    width          : '100%'
  }, {
    color         : dlsColor('darkestBlue'),
    fontSize      : '13px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    textTransform : 'capitalize'
  }),
  labelRoot : toggleThemeStyle({
    fontSize       : '12px !important',
    color          : dlsColor('aaBlue'),
    justifyContent : 'flex-start',
    width          : '100%',
    '&:hover'      : {
      textDecoration : 'underline'
    }
  }, {
    color                                  : `${dlsColor('grey1')} !important`,
    width                                  : '100%',
    fontSize                               : '14px',
    fontFamily                             : 'Inter !important',
    fontWeight                             : 500,
    lineHeight                             : '20px !important',
    letterSpacing                          : '0px',
    justifyContent                         : 'flex-start !important' as any,
    '& .MuiGrid-justify-content-xs-center' : {
      justifyContent : 'flex-start !important' as any
    },
    '& .MuiGrid-align-items-xs-center' : {
      alignItems : 'flex-start !important' as any
    },
    '& .Mui-disabled' : {
      color : `${dlsColor('grey4')} !important`
    }
  }),
  selectedQueryName : {
    backgroundColor : dlsColor('lightBlue'),
    '&:hover'       : {
      backgroundColor : dlsColor('lightBlue')
    }
  },
  dropdownActions : {
    height : '208px'
  }
}));

export interface SearchOperationsDropDownProps {
  queryName?: string;
  queries: any;
  pageName: string;
  onSaveClick: () => void;
  onSave: () => void;
  onUndoChanges: (queryName: string) => void;
  onRemove: () => void;
  onSavedQuerySelect: (args: any) => void;
}

export const SearchOperationsDropDown = ({
  queries,
  pageName,
  queryName,
  onSaveClick,
  onSave,
  onUndoChanges,
  onRemove,
  onSavedQuerySelect
}: SearchOperationsDropDownProps) => {
  const classes                 = useStyles();
  const saveContainerRef        = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const canShowQueryOperations  = useMemo(() => queries?.length > 0, [queries]);
  const savedQueries            = removeTempQueries(pageName);

  const savedQueryUpdated = useMemo(() => {
    if (!isNil(queryName)) {
      const queriesForNameStringified = savedQueries?.query?.filter(
        ({name}) => name === queryName)?.[0];
      if (queriesForNameStringified?.query) {
        const savedQueryLabels = safeJSONParse(
          queriesForNameStringified?.query
        )?.map(({label}) => label);
        const queryLabels = queries?.map(({label}) => label);
        return !isEqualArrays(queryLabels, savedQueryLabels);
      }
      return false;
    }
    return false;
  }, [queries, queryName, savedQueries]);

  const isNewQuery = useMemo(() => {
    const queriesForNameStringified = savedQueries?.query?.filter?.(
      ({name}) => name === queryName)?.[0];
    if (!isNil(queryName) && queriesForNameStringified?.query) {
      const savedQueryLabels = safeJSONParse(
        queriesForNameStringified?.query)?.map(({label}) => label);
      const queryLabels = queries.map(({label}) => label);
      return !isEqualArrays(queryLabels, savedQueryLabels);
    }
    return queries?.length > 0;
  }, [queries, queryName, savedQueries]);

  const onSavedQueryItemSelect = useCallback(function(props: any) {
    setAnchorEl(null);
    onSavedQuerySelect(props);
  }, []);

  const onIconClick = useCallback(function() {
    setAnchorEl(saveContainerRef.current);
  }, []);

  const handleClose = useCallback(function() {
    setAnchorEl(null);
  }, []);

  const onSaveNew = useCallback(function() {
    handleClose();
    onSaveClick();
  }, []);

  const undoCurrentChanges = useCallback(function() {
    onUndoChanges(queryName);
    setAnchorEl(null);
  }, [queryName]);

  const onSaveChanges = useCallback(function() {
    handleClose();
    onSave();
  }, [savedQueries, queries, queryName, pageName]);

  const onRemoveQuery = useCallback(function() {
    handleClose();
    onRemove();
  }, [onRemove]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <>
        <div ref={saveContainerRef}
          className={classes.selectContainer}>
          <Button id={SELECT_FROM_SAVED_QUERY_BUTTON}
            classes={{root: classes.saveBtn}}
            onClick={onIconClick}
            endIcon={<DropdownArrowIcon />}>
            <Label keyItem={'savedSearch'}
              classes={classes}
              className={'openText'}
              text={queryName} />
          </Button>
        </div>
        <Menu id={QUERY_OPERATIONS_DROPDOWN}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          classes={{list: classes.list}}
          PopoverClasses={{
            paper : classes.popoverRoot
          }}
          anchorOrigin={{
            vertical   : 'bottom',
            horizontal : 'left'
          }}
          transformOrigin={{
            vertical   : 'top',
            horizontal : 'left'
          }}
          onClose={handleClose}>
          {(!isNil(queryName) || canShowQueryOperations) && (
            <QueryOperations queryName={queryName}
              classes={classes}
              isNewQuery={isNewQuery}
              savedQueryUpdated={savedQueryUpdated}
              onSaveChanges={onSaveChanges}
              onSaveNew={onSaveNew}
              undoCurrentChanges={undoCurrentChanges}
              onRemove={onRemoveQuery} />)}
          <SaveQueriesList pageName={pageName}
            queryName={queryName}
            onSavedQuerySelect={onSavedQueryItemSelect} />
        </Menu>
      </>
    </ClickAwayListener>
  );
};
